/* ==========================================================================
   Orbly V2 — Dark / Light Mode Toggle
   FOLT-safe: data-theme is set by inline <head> script before paint.

   SHO-227 v3: Aggressive iOS Safari URL-bar refresh on toggle.
   v2 (media-query swap) didn't fix PDP — works on landing but iOS Safari
   refused to re-tint URL bar after toggle on PDP. Issue is iOS-specific:
   even when JS correctly flips media="all" between light/dark metas, iOS
   keeps the cached load-time tint until a "qualifying event" happens.

   On PDP that qualifying event apparently never fires from the toggle
   alone (different render pattern than landing). v3 adds explicit triggers:
     - Re-append metas to the end of <head> (DOM tree mutation)
     - Title bump (forces page metadata re-eval)
     - Programmatic scroll (URL-bar minimize/expand cycle on iOS)
     - Lang attribute kick (sometimes flushes iOS view state)
   ========================================================================== */
(function(){
  'use strict';
  try{localStorage.removeItem('orbly-theme')}catch(e){}
  var KEY='orbly-theme-v2';
  var IS_IOS=typeof navigator!=='undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent||'');

  function sys(){
    var mq=window.matchMedia&&window.matchMedia('(prefers-color-scheme:dark)');
    return (mq&&mq.matches)?'dark':'light';
  }
  function stored(){try{return localStorage.getItem(KEY)}catch(e){return null}}
  function cur(){return document.documentElement.getAttribute('data-theme')||sys()}

  // Keep browser chrome gold in both modes. iOS Safari can otherwise cache the
  // dark load-time tint and keep the top bar black after dark -> light toggles.
  var BROWSER_BAR_COLOR='#bfa06b';
  var COLORS={dark:BROWSER_BAR_COLOR,light:BROWSER_BAR_COLOR};

  /* Update metadata used by iOS Safari: media-query fallbacks plus one
     active unqualified theme-color meta and color-scheme order. */
  function syncThemeMetadata(mode){
    var lm=document.getElementById('orbly-theme-color-light');
    var dm=document.getElementById('orbly-theme-color-dark');
    var active=document.getElementById('orbly-theme-color-active');
    var scheme=document.getElementById('orbly-color-scheme');
    var color=COLORS[mode]||COLORS.light;
    var colorScheme=mode==='dark'?'dark light':'light dark';

    if(lm) lm.setAttribute('media', mode==='light' ? 'all' : 'not all');
    if(dm) dm.setAttribute('media', mode==='dark'  ? 'all' : 'not all');
    if(active) active.setAttribute('content', color);
    if(scheme) scheme.setAttribute('content', colorScheme);

    var h=document.documentElement;
    var b=document.body;
    h.style.colorScheme=mode;
    h.style.setProperty('--orbly-browser-bar-color', color);
    if(b) b.style.colorScheme=mode;

    return [lm,dm,scheme,active].filter(Boolean);
  }

  function ensureActiveMeta(color){
    var active=document.getElementById('orbly-theme-color-active');
    if(!active){
      active=document.createElement('meta');
      active.id='orbly-theme-color-active';
      active.setAttribute('name','theme-color');
      document.head.appendChild(active);
    }
    active.setAttribute('content', color);
    if(active.parentNode) active.parentNode.appendChild(active);
    return active;
  }

  function replaceRuntimeThemeColorMeta(color){
    var old=document.querySelectorAll('meta[data-orbly-runtime-theme-color]');
    for(var i=0;i<old.length;i++) old[i].parentNode&&old[i].parentNode.removeChild(old[i]);
    var fresh=document.createElement('meta');
    fresh.setAttribute('name','theme-color');
    fresh.setAttribute('content',color);
    fresh.setAttribute('data-orbly-runtime-theme-color','true');
    document.head.appendChild(fresh);
    return fresh;
  }

  function flashIOSChromeSampler(mode){
    if(!IS_IOS)return;
    var color=COLORS[mode]||COLORS.light;
    var cover=document.getElementById('orbly-ios-chrome-sampler');
    if(!cover){
      cover=document.createElement('div');
      cover.id='orbly-ios-chrome-sampler';
      cover.setAttribute('aria-hidden','true');
      cover.style.cssText='position:fixed;left:0;right:0;top:0;height:calc(env(safe-area-inset-top,0px) + 96px);z-index:2147483647;pointer-events:none;background:var(--orbly-browser-bar-color);opacity:1;transition:opacity .12s linear;';
      document.body&&document.body.appendChild(cover);
    }
    cover.style.background=color;
    cover.style.opacity='1';
    requestAnimationFrame(function(){
      requestAnimationFrame(function(){ cover.style.opacity='0'; });
    });
  }

  /* Multi-trigger iOS Safari URL-bar refresh. PDP needed a stronger mutation
     than landing: replace one runtime meta node, update root color-scheme style,
     and briefly present a top sampler color for WebKit's chrome tint pass. */
  function nudgeIOSStatusBar(mode,metas){
    try{
      var color=COLORS[mode]||COLORS.light;
      metas.forEach(function(m){
        if(m && m.id!=='orbly-theme-color-active' && m.parentNode) m.parentNode.appendChild(m);
      });
      ensureActiveMeta(color);
      replaceRuntimeThemeColorMeta(color);
      void document.documentElement.offsetHeight;

      var t=document.title;
      document.title = t + '​';
      document.title = t;

      var html=document.documentElement;
      var l=html.getAttribute('lang')||'en';
      html.setAttribute('lang','x-'+l);
      requestAnimationFrame(function(){ html.setAttribute('lang', l); });

      if(IS_IOS){
        flashIOSChromeSampler(mode);
        setTimeout(function(){ ensureActiveMeta(color); replaceRuntimeThemeColorMeta(color); },60);
        setTimeout(function(){ ensureActiveMeta(color); replaceRuntimeThemeColorMeta(color); },220);
      }
    }catch(e){}
  }

  function setActiveThemeMeta(mode){
    var metas=syncThemeMetadata(mode);
    if(!metas.length){ metas=[ensureActiveMeta(COLORS[mode]||COLORS.light)]; }
    nudgeIOSStatusBar(mode,metas);
  }

  function apply(mode,anim){
    var h=document.documentElement;
    if(anim){h.classList.add('theme-transitioning');setTimeout(function(){h.classList.remove('theme-transitioning')},500)}
    h.setAttribute('data-theme',mode);

    if(mode===sys()){
      try{localStorage.removeItem(KEY)}catch(e){}
    }else{
      try{localStorage.setItem(KEY,mode)}catch(e){}
    }

    setActiveThemeMeta(mode);

    var toggles=document.querySelectorAll('.dark-toggle');
    for(var i=0;i<toggles.length;i++)toggles[i].setAttribute('aria-label',mode==='dark'?'Switch to light mode':'Switch to dark mode');
  }

  function init(){
    var s=stored();
    if(s && s===sys()){
      try{localStorage.removeItem(KEY)}catch(e){}
    }
    apply(s && s!==sys() ? s : sys(), false);

    document.addEventListener('click',function(e){
      var b=e.target.closest('.dark-toggle');
      if(!b)return;
      apply(cur()==='dark'?'light':'dark',true);
    });

    var mq=window.matchMedia&&window.matchMedia('(prefers-color-scheme:dark)');
    if(mq){
      var handler=function(e){
        if(!stored()){
          apply(e.matches?'dark':'light',false);
        }
      };
      if(mq.addEventListener)mq.addEventListener('change',handler);
      else if(mq.addListener)mq.addListener(handler);
    }
  }

  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);
  else init();
})();
